/**
 * Compute fees (firm-wide) — Admin batch page (#17 Inc D → per-account arc Inc 3).
 *
 * ONE context-aware step (Pedro 2026-07-11, B-ui-c — collapses the old Preview +
 * Recompute-month button pair): PREVIEW a billing month (compute the per-account approval
 * list without writing) → review → FREEZE the not-yet-frozen agreements. Recompute is NOT a
 * second top button: if the month already has frozen agreements, the preview surfaces a
 * "Recompute N frozen" action (delete + re-freeze from the current 20th-AuM, with an
 * invoice-conflict confirm) — shown only when it is meaningful. Forward-only otherwise;
 * agreements missing a contributing account's 20th-AuM are listed (skip + loud flag) so the
 * operator can fill them on the "1 · Upload month" tab and re-preview.
 */
import { useEffect, useState } from 'react';
import {
  Accordion, ActionIcon, Alert, Badge, Button, Container, Group, List, Modal, Paper, Stack, Table, Text,
  Textarea, Title, Tooltip,
} from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconBan, IconCalculator, IconCheck, IconFileSpreadsheet, IconRefresh,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  feeRunsApi, type Exclusion, type FeeExportReport, type FeeRunBatchPreview, type FeeRunBatchResult,
  type FeeRunPreviewRow, type NotDue,
} from '../api/feeRuns';
import { feeInvoicesApi, type FeeHistoryRow } from '../api/feeInvoices';
import { bpsToPct, fmtEurFromCents } from '../api/feeSchedules';
import { RequireAccess } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { useAlertModal } from '../components/AlertModalProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function FeeRunBatchPage() {
  useDocumentTitle('Compute fees');
  return (
    <RequireAccess roles={['admin']}>
      <Container size="xl">
        <Stack gap="lg">
          <BackToCompliance to="/admin" label="Back to Admin" />
          <BatchRunner />
        </Stack>
      </Container>
    </RequireAccess>
  );
}

// Exported so the new tabbed Fees page (/admin/fees) can render the
// Compute-Fees content inside its "Compute Month" tab. The standalone
// FeeRunBatchPage + its /admin/fee-run-batch route stay working as-is.
export function BatchRunner({ seedMonth, onSeedConsumed }: { seedMonth?: string; onSeedConsumed?: () => void } = {}) {
  const qc = useQueryClient();
  const [month, setMonth] = useState(seedMonth ?? '');
  const [preview, setPreview] = useState<FeeRunBatchPreview | null>(null);
  const [result, setResult] = useState<FeeRunBatchResult | null>(null);
  const [recomputeMode, setRecomputeMode] = useState(false);   // preview is a recompute preview
  const [recomputeConfirm, setRecomputeConfirm] = useState(false);
  const [recomputePreview, setRecomputePreview] = useState<FeeRunBatchPreview | null>(null);  // feeds the confirm; NOT rendered as a page (#2 refinement, Pedro 2026-07-12)

  // Hand-off from the 20th-AuM upload pre-selects the just-uploaded month (#4, Pedro 2026-07-12);
  // one-shot — consumed on mount so a later manual visit to Compute opens with a blank picker.
  useEffect(() => {
    if (seedMonth) onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The runs change firm-wide — invalidate every agreement's frozen runs + the
  // View-History feed so they refetch when next viewed. (The old current-fee
  // summary was retired with the Slice 5 cutover.)
  const invalidateRuns = () => {
    qc.invalidateQueries({ queryKey: ['agreement-fee-runs'] });
    qc.invalidateQueries({ queryKey: ['fee-history'] });
  };

  const previewMut = useMutation({
    mutationFn: () => feeRunsApi.previewBatch(month),
    onSuccess: (p) => { setPreview(p); setResult(null); setRecomputeMode(false); },
  });
  const recomputePreviewMut = useMutation({
    mutationFn: () => feeRunsApi.recomputeBatchPreview(month),
    // Straight to the confirm — the preview runs only to populate the conflict list; it is not
    // drawn as its own page (the middle "Recompute N runs" click did nothing, Pedro 2026-07-12).
    onSuccess: (p) => { setRecomputePreview(p); setRecomputeConfirm(true); },
  });
  const freezeMut = useMutation({
    mutationFn: () => feeRunsApi.computeBatch(month),
    onSuccess: (r) => { setResult(r); setPreview(null); invalidateRuns(); },
  });
  const recomputeMut = useMutation({
    mutationFn: () => feeRunsApi.recomputeBatch(month),
    onSuccess: (r) => {
      setResult(r); setPreview(null); setRecomputeMode(false); setRecomputeConfirm(false);
      setRecomputePreview(null); invalidateRuns();
    },
  });

  // ── Accountant export (arc Inc 4) — gated on completeness / drift ────────
  const period = month ? month.slice(0, 7) : '';   // 'YYYY-MM' for the export endpoints
  const [exporting, setExporting] = useState(false);
  const [exportGate, setExportGate] = useState<FeeExportReport | null>(null);

  async function downloadNow() {
    setExporting(true);
    try {
      await feeRunsApi.downloadExport(period);
      setExportGate(null);
    } catch (e) {
      notifications.show({ color: 'red', title: 'Export failed', message: e instanceof Error ? e.message : 'Could not export.' });
    } finally { setExporting(false); }
  }

  async function onExportClick() {
    setExporting(true);
    try {
      const rep = await feeRunsApi.exportReport(period);
      if (rep.total_rows === 0) {
        notifications.show({ color: 'gray', message: `No frozen fee runs for ${rep.period_month} — freeze the month first.` });
        return;
      }
      if (rep.incomplete.length || rep.drift.length) {
        setExportGate(rep);                // gate: show the issues, let the user proceed / cancel
      } else {
        await feeRunsApi.downloadExport(period);
      }
    } catch (e) {
      notifications.show({ color: 'red', title: 'Export failed', message: e instanceof Error ? e.message : 'Could not export.' });
    } finally { setExporting(false); }
  }

  const anyIncomplete = (preview?.rows ?? []).some((r) => !r.billing_complete);
  // Defensive: a pre-recompute backend (not yet restarted) omits invoice_conflicts —
  // never let a missing field white-screen the page (the modal title evaluates this
  // on every render once a preview is set).
  const conflicts = recomputePreview?.invoice_conflicts ?? [];

  // Is the chosen month already computed? Drives the toolbar (#2, Pedro 2026-07-12): a frozen
  // month opens straight into the frozen view (Recompute + Export, no Preview), and the recompute
  // confirm escalates when invoices for the month are already issued / settled. Shares the
  // ReviewAndWaive cache (same query key), so it costs no extra fetch.
  const monthHistoryQ = useQuery({
    queryKey: ['fee-history', 'month', period],
    queryFn: () => feeInvoicesApi.listMonthHistory(period),
    enabled: !!period,
  });
  const monthRows: FeeHistoryRow[] = Array.isArray(monthHistoryQ.data) ? monthHistoryQ.data : [];
  const monthHasRuns = monthRows.length > 0;
  const issuedCount = monthRows.filter((r) => r.status === 'issued').length;
  const settledCount = monthRows.filter((r) => r.status === 'settled').length;

  return (
    <Stack>
      <Group gap="xs"><IconCalculator size={20} /><Title order={3}>Compute fees — firm-wide</Title></Group>
      <Text c="dimmed" size="sm">
        A <b>new</b> month: <b>Preview</b> the per-account approval list (computed without saving), then
        <b>Freeze</b> to write the runs. An <b>already-computed</b> month opens straight into review — with
        <b>Recompute</b> (redo after editing the 20th-AuM) and <b>Export</b>. Agreements missing a
        contributing account's 20th-of-month AuM are listed so you can add it on the "1 · Upload month"
        tab and re-preview.
      </Text>
      <Group align="flex-end">
        <MonthPickerInput label="Billing month" placeholder="Pick a month"
          minDate={new Date('2026-01-01T00:00:00')}
          value={month ? new Date(`${month}T00:00:00`) : null}
          onChange={(d) => {
            setMonth(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01` : '');
            setPreview(null);
            setResult(null);
            setRecomputeMode(false);
            setRecomputePreview(null);
          }}
          maw={200} />
        {!monthHasRuns && (
          <Button leftSection={<IconCalculator size={16} />} disabled={!month} loading={previewMut.isPending}
            onClick={() => previewMut.mutate()}>
            Preview
          </Button>
        )}
        {monthHasRuns && (
          <Tooltip label="Redo this already-frozen month after editing the 20th-AuM — recomputes every agreement and re-freezes" withArrow multiline w={260}>
            <Button variant="light" color="orange" leftSection={<IconRefresh size={16} />} disabled={!month}
              loading={recomputePreviewMut.isPending} onClick={() => recomputePreviewMut.mutate()}>
              Recompute month
            </Button>
          </Tooltip>
        )}
        {/* Export is shown only once the month has frozen runs — before that there is nothing to
            export, so the button would only ever toast "freeze the month first" (#2). */}
        {monthHasRuns && (
          <Button variant="default" leftSection={<IconFileSpreadsheet size={16} />} disabled={!month} loading={exporting}
            onClick={onExportClick}>
            Export for accountants
          </Button>
        )}
      </Group>

      {previewMut.isError && (
        <Alert color="red">{previewMut.error instanceof ApiError ? previewMut.error.detail : 'Preview failed.'}</Alert>
      )}
      {recomputePreviewMut.isError && (
        <Alert color="red">{recomputePreviewMut.error instanceof ApiError ? recomputePreviewMut.error.detail : 'Recompute preview failed.'}</Alert>
      )}
      {freezeMut.isError && (
        <Alert color="red">{freezeMut.error instanceof ApiError ? freezeMut.error.detail : 'Freeze failed.'}</Alert>
      )}
      {recomputeMut.isError && (
        <Alert color="red">{recomputeMut.error instanceof ApiError ? recomputeMut.error.detail : 'Recompute failed.'}</Alert>
      )}

      {/* Already-computed month opens straight into review — no Preview needed (#2, Pedro #11). */}
      {monthHasRuns && !preview && !result && (
        <Alert color="blue" variant="light" icon={<IconCalculator size={16} />}>
          Fees are <b>computed and frozen</b> for {fmtMonthShort(period)}. Review &amp; waive below, or Export for
          accountants. <b>Recompute month</b> (after editing the 20th-AuM) replaces the frozen runs and may change
          fees already invoiced.
        </Alert>
      )}

      {preview && (
        <PreviewReport
          p={preview}
          anyIncomplete={anyIncomplete}
          recomputeMode={recomputeMode}
          busy={freezeMut.isPending || recomputeMut.isPending}
          onAction={recomputeMode ? () => setRecomputeConfirm(true) : () => freezeMut.mutate()}
          onRecomputeFrozen={!recomputeMode ? () => recomputePreviewMut.mutate() : undefined}
          recomputing={recomputePreviewMut.isPending}
        />
      )}
      {result && <FreezeReport r={result} />}

      {month && <ReviewAndWaive month={month} />}

      <Modal opened={!!exportGate} onClose={() => setExportGate(null)} size="lg"
        title="Billing details need attention before export">
        {exportGate && (
          <Stack gap="sm">
            <Text size="sm">
              {exportGate.total_rows} frozen row(s) for {exportGate.period_month}. The following will export with
              gaps — fix them on each agreement's Fees tab, or export anyway.
            </Text>
            {exportGate.incomplete.length > 0 && (
              <div>
                <Text fw={600} size="sm" c="orange">Incomplete billing details ({exportGate.incomplete.length})</Text>
                <List size="sm">{exportGate.incomplete.map((l) => <List.Item key={`i-${l}`}>{l}</List.Item>)}</List>
              </div>
            )}
            {exportGate.drift.length > 0 && (
              <div>
                <Text fw={600} size="sm" c="orange">Drift vs the linked party ({exportGate.drift.length})</Text>
                <List size="sm">{exportGate.drift.map((l) => <List.Item key={`d-${l}`}>{l}</List.Item>)}</List>
              </div>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setExportGate(null)}>Cancel &amp; go edit</Button>
              <Button color="orange" loading={exporting} onClick={downloadNow}>Export anyway</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={recomputeConfirm} onClose={() => setRecomputeConfirm(false)} size="lg"
        title={settledCount > 0
          ? 'Recompute will affect SETTLED invoices'
          : issuedCount > 0
            ? 'Recompute will affect issued invoices'
            : conflicts.length > 0
              ? 'Recompute will conflict with uploaded invoices'
              : `Recompute ${recomputePreview?.period_month?.slice(0, 7) ?? 'this month'}?`}>
        <Stack gap="sm">
          {/* Tiered stakes (#2): settled (paid) is loudest, then issued, then value drift. */}
          {settledCount > 0 && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}
              title={`${settledCount} invoice(s) for this month are already SETTLED (paid)`}>
              <Text size="sm">
                Recomputing changes the frozen fees those settled invoices were issued against. In the real world
                that means <b>annulling the settled invoices with credit notes and re-invoicing</b> the difference,
                then reconciling the client payments. Only proceed if you are prepared to manage that.
              </Text>
            </Alert>
          )}
          {settledCount === 0 && issuedCount > 0 && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />}
              title={`${issuedCount} invoice(s) for this month are already issued`}>
              <Text size="sm">
                Recomputing changes fees that have already been invoiced — each affected invoice's cross-check will
                flag a mismatch until it is re-issued.
              </Text>
            </Alert>
          )}
          {conflicts.length > 0 ? (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={700} mb={4}>
                {conflicts.length} uploaded invoice(s) will NO LONGER match the recomputed fee:
              </Text>
              <List size="sm">{conflicts.map((c, i) => <List.Item key={i}>{c}</List.Item>)}</List>
            </Alert>
          ) : (
            <Text size="sm">No uploaded invoices conflict with the recomputed values.</Text>
          )}
          <Text size="sm" c="dimmed">
            This deletes the frozen fee runs for {recomputePreview?.period_month?.slice(0, 7)} and re-freezes them from the
            current 20th-AuM. Uploaded invoices are kept; their cross-check re-evaluates against the new runs.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRecomputeConfirm(false)}>Cancel</Button>
            <Button color={settledCount > 0 || conflicts.length > 0 ? 'red' : 'orange'}
              loading={recomputeMut.isPending} onClick={() => recomputeMut.mutate()}>
              Recompute &amp; freeze
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

interface AgrGroup {
  id: string;
  code: string;
  codeKey: string;
  mnemonic: string;
  rows: FeeRunPreviewRow[];
  sumAum: number;
  sumNet: number;
  sumTotal: number;
  anyIncomplete: boolean;
}

/** Roll the flat per-account preview up per agreement, sorted by agreement code
 *  (CIIF-nnn — zero-padded, so a string sort is numerically correct), not mnemonic. */
function groupByAgreement(rows: FeeRunPreviewRow[]): AgrGroup[] {
  const map = new Map<string, AgrGroup>();
  for (const r of rows) {
    let g = map.get(r.agreement_id);
    if (!g) {
      g = {
        id: r.agreement_id, code: r.agreement_code || '—', codeKey: r.agreement_code || '',
        mnemonic: r.agreement_mnemonic || '', rows: [], sumAum: 0, sumNet: 0, sumTotal: 0, anyIncomplete: false,
      };
      map.set(r.agreement_id, g);
    }
    g.rows.push(r);
    g.sumAum += r.aum_eur_cents ?? 0;
    g.sumNet += r.net_fee_cents;
    g.sumTotal += r.total_fee_cents;
    if (!r.billing_complete) g.anyIncomplete = true;
  }
  return [...map.values()].sort((a, b) => (a.codeKey < b.codeKey ? -1 : a.codeKey > b.codeKey ? 1 : 0));
}

function PreviewReport({
  p, anyIncomplete, recomputeMode, busy, onAction, onRecomputeFrozen, recomputing,
}: {
  p: FeeRunBatchPreview; anyIncomplete: boolean; recomputeMode: boolean; busy: boolean;
  onAction: () => void; onRecomputeFrozen?: () => void; recomputing?: boolean;
}) {
  const totalNet = p.rows.reduce((s, r) => s + r.net_fee_cents, 0);
  const groups = groupByAgreement(p.rows);
  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end">
        <Text fw={600}>
          {recomputeMode ? 'Recompute preview' : 'Approval list'} for {p.period_month} — {groups.length} agreement(s), {p.rows.length} account run(s)
        </Text>
        {p.rows.length > 0 && (
          <Button color={recomputeMode ? 'orange' : 'verde'} leftSection={<IconCheck size={16} />} loading={busy} onClick={onAction}>
            {recomputeMode ? 'Recompute' : 'Freeze'} {p.rows.length} run(s)
          </Button>
        )}
      </Group>

      {recomputeMode && (p.invoice_conflicts?.length ?? 0) > 0 && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}
          title={`${p.invoice_conflicts?.length ?? 0} uploaded invoice(s) will conflict with the recomputed fee`}>
          Recomputing changes fees that already have an uploaded invoice — their cross-check will flag a mismatch.
          You'll see the exact list and confirm before it overwrites the frozen runs.
        </Alert>
      )}

      {anyIncomplete && (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          Some rows have incomplete billing details. Fees still compute and freeze; the
          accountant Excel export flags these before invoicing.
        </Alert>
      )}

      {groups.length > 0 ? (
        <>
          {/* Rolled up per agreement (sorted by code); click to expand the per-account detail. */}
          <Accordion variant="separated" multiple chevronPosition="left">
            {groups.map((g) => (
              <Accordion.Item key={g.id} value={g.id}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap" pr="md">
                    <Group gap="xl" wrap="nowrap">
                      <Stack gap={0}><Text size="xs" c="dimmed">Code</Text><Text fw={600}>{g.code}</Text></Stack>
                      <Stack gap={0}><Text size="xs" c="dimmed">Mnemonic</Text><Text>{g.mnemonic || '—'}</Text></Stack>
                    </Group>
                    <Group gap="lg" wrap="nowrap">
                      <AgrTotal label="Σ AuM" cents={g.sumAum} />
                      <AgrTotal label="Σ Net" cents={g.sumNet} />
                      <AgrTotal label="Σ Total" cents={g.sumTotal} strong />
                      <Text size="xs" c="dimmed">{g.rows.length} acct</Text>
                      {g.anyIncomplete && (
                        <Tooltip withArrow multiline w={280}
                          label="Billing name, TIN or address is missing — the fee computes, but the invoice cannot be issued correctly. Complete it on Admin → Fees → Billing details.">
                          <Badge size="sm" color="orange" variant="light" leftSection={<IconAlertTriangle size={12} />}>
                            incomplete identity
                          </Badge>
                        </Tooltip>
                      )}
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Table.ScrollContainer minWidth={760}>
                    <Table withTableBorder striped verticalSpacing="xs">
                      <Table.Thead><Table.Tr>
                        <Table.Th>Account</Table.Th>
                        <Table.Th>Billing payee</Table.Th>
                        <Table.Th ta="right">AuM</Table.Th>
                        <Table.Th ta="right">Rate</Table.Th>
                        <Table.Th ta="right">Fee € (ex VAT)</Table.Th>
                        <Table.Th ta="right">Incl VAT</Table.Th>
                      </Table.Tr></Table.Thead>
                      <Table.Tbody>
                        {g.rows.map((r) => <PreviewAccountRow key={r.account_id} r={r} />)}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
          <Group justify="flex-end" pr="md">
            <Text fw={600}>Total ex VAT: €{fmtEurFromCents(totalNet)}</Text>
          </Group>
        </>
      ) : (
        <Text c="dimmed">No agreements are ready to compute for this month.</Text>
      )}

      {!recomputeMode && p.skipped_existing.length > 0 && onRecomputeFrozen ? (
        <Paper withBorder p="sm" radius="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div>
              <Text fw={600} c="dimmed">Already frozen this month: {p.skipped_existing.length}</Text>
              <Text size="xs" c="dimmed">{p.skipped_existing.join(' · ')}</Text>
            </div>
            <Tooltip
              label="Edited a frozen agreement's 20th-AuM? Recompute deletes its frozen runs and re-freezes from the current AuM — you'll review any invoice conflicts and confirm first."
              withArrow multiline w={300}>
              <Button variant="light" color="orange" leftSection={<IconRefresh size={16} />}
                loading={recomputing} onClick={onRecomputeFrozen} style={{ flexShrink: 0 }}>
                Recompute {p.skipped_existing.length} frozen
              </Button>
            </Tooltip>
          </Group>
        </Paper>
      ) : (
        <Bucket title="Already frozen (skipped)" items={p.skipped_existing} color="dimmed" />
      )}
      <Bucket title="Skipped — missing AuM / no Active account" items={p.skipped_no_aum} color="laranja.7" />
      <Bucket title="Not due this month (off-cycle cadence)" items={p.not_due ?? []} color="dimmed" />
      <Bucket title="Not started — no fee-basis data yet (new, or first upload missing?)" items={p.not_started ?? []} color="laranja.7" />
      <Bucket title="Errors" items={p.errors} color="red" />
    </Stack>
  );
}

function AgrTotal({ label, cents, strong }: { label: string; cents: number; strong?: boolean }) {
  return (
    <Stack gap={0} align="flex-end">
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="sm" fw={strong ? 700 : 500}>€{fmtEurFromCents(cents)}</Text>
    </Stack>
  );
}

function PreviewAccountRow({ r }: { r: FeeRunPreviewRow }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          <Text size="sm">{r.bank_name || 'Account'}{r.account_number ? ` — ${r.account_number}` : ''}</Text>
          <Badge size="xs" variant="light" color={r.is_direct ? 'blue' : 'grape'}>
            {r.is_direct ? 'Direct' : 'Indirect'}
          </Badge>
        </Group>
        {!r.is_direct && (r.insurer || r.policy_number) && (
          <Text size="xs" c="dimmed">{r.insurer ?? 'Insurer'}{r.policy_number ? ` · ${r.policy_number}` : ''}</Text>
        )}
      </Table.Td>
      <Table.Td>
        {r.billing_name ? <Text size="sm">{r.billing_name}</Text> : <Text size="sm" c="dimmed">—</Text>}
        {!r.billing_complete && (
          <Tooltip label="Billing details incomplete (name / TIN / address)" withArrow>
            <Badge size="xs" color="orange" variant="light" leftSection={<IconAlertTriangle size={10} />}>incomplete</Badge>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td ta="right">
        <Text size="sm">{r.aum_eur_cents != null ? `€${fmtEurFromCents(r.aum_eur_cents)}` : '—'}</Text>
      </Table.Td>
      <Table.Td ta="right">
        <Text size="sm">{r.effective_rate_bps != null ? `${bpsToPct(r.effective_rate_bps).toFixed(2)} %` : '—'}</Text>
      </Table.Td>
      <Table.Td ta="right"><Text size="sm" fw={600}>€{fmtEurFromCents(r.net_fee_cents)}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm" c="dimmed">€{fmtEurFromCents(r.total_fee_cents)}</Text></Table.Td>
    </Table.Tr>
  );
}

function Bucket({ title, items, color }: { title: string; items: string[]; color: string }) {
  if (items.length === 0) return null;
  return (
    <Paper withBorder p="sm" radius="md">
      <Text fw={600} c={color}>{title}: {items.length}</Text>
      <Text size="xs" c="dimmed">{items.join(' · ')}</Text>
    </Paper>
  );
}

function FreezeReport({ r }: { r: FeeRunBatchResult }) {
  const empty = r.computed.length === 0 && r.skipped_existing.length === 0
    && r.skipped_no_aum.length === 0 && r.errors.length === 0
    && (r.not_due?.length ?? 0) === 0 && (r.not_started?.length ?? 0) === 0;
  return (
    <Stack gap="sm">
      <Text fw={600}>Frozen for {r.period_month}</Text>
      <Bucket title="Computed + frozen" items={r.computed} color="verde.7" />
      <Bucket title="Already frozen (skipped)" items={r.skipped_existing} color="dimmed" />
      <Bucket title="Skipped — missing AuM / no Active account" items={r.skipped_no_aum} color="laranja.7" />
      <Bucket title="Not due this month (off-cycle cadence)" items={r.not_due ?? []} color="dimmed" />
      <Bucket title="Not started — no fee-basis data yet (new, or first upload missing?)" items={r.not_started ?? []} color="laranja.7" />
      <Bucket title="Errors" items={r.errors} color="red" />
      {empty && <Text c="dimmed">No agreements have a fee basis set.</Text>}
    </Stack>
  );
}

// ── Review & waive (Pedro 2026-07-10, Option B) — between Freeze and Export ────
// The waive decision lives HERE (compute → waive → export), not in the review-only
// History tab. Waiving DELETES the run + records an exclusion, so a waived fee can't
// leak into any total/report; restore recomputes the account-month back to billable.

/** Friendly account label — Direct: bank · number; Indirect: bank · number · insurer · policy#.
 * Bank·account is shown for INDIRECT too so the underlying account is identifiable (Pedro
 * 2026-07-24; §3h consistency with the fee-history / settle viewer). */
function acctLabel(r: {
  is_direct: boolean; bank_name: string | null; account_number: string | null;
  insurer: string | null; policy_number: string | null;
}): string {
  const bankAcct = r.bank_name
    ? (r.account_number ? `${r.bank_name} · ${r.account_number}` : r.bank_name)
    : null;
  if (r.is_direct) return bankAcct || 'Account';
  const insurer = r.insurer || 'Policy';
  const policy = r.policy_number ? `${insurer} · ${r.policy_number}` : insurer;
  return bankAcct ? `${bankAcct} · ${policy}` : policy;
}

function fmtMonthShort(periodMonth: string): string {
  const ym = periodMonth.slice(0, 7);
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function ReviewAndWaive({ month }: { month: string }) {
  const qc = useQueryClient();
  const { showAlert } = useAlertModal();
  const period = month.slice(0, 7);
  const [waiveRow, setWaiveRow] = useState<FeeHistoryRow | null>(null);

  const runsQ = useQuery({
    queryKey: ['fee-history', 'month', period],
    queryFn: () => feeInvoicesApi.listMonthHistory(period),
    enabled: !!period,
  });
  const exclQ = useQuery({
    queryKey: ['fee-exclusions', period],
    queryFn: () => feeRunsApi.listExclusions(period),
    enabled: !!period,
  });
  const notDueQ = useQuery({
    queryKey: ['fee-not-due', period],
    queryFn: () => feeRunsApi.listNotDue(period),
    enabled: !!period,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['fee-history'] });
    qc.invalidateQueries({ queryKey: ['fee-exclusions'] });
    qc.invalidateQueries({ queryKey: ['agreement-fee-runs'] });
  };

  const waiveMut = useMutation({
    mutationFn: (vars: { runId: string; reason: string | null }) => feeRunsApi.exclude(vars.runId, vars.reason),
    onSuccess: () => { notifications.show({ color: 'green', message: 'Fee waived — not billed.' }); setWaiveRow(null); invalidate(); },
    onError: (e) => showAlert({ color: 'red', title: 'Could not waive fee', message: e instanceof Error ? e.message : 'Unknown error' }),
  });
  const restoreMut = useMutation({
    mutationFn: (exclusionId: string) => feeRunsApi.restore(exclusionId),
    onSuccess: () => { notifications.show({ color: 'green', message: 'Fee restored — billable again.' }); invalidate(); },
    onError: (e) => showAlert({ color: 'red', title: 'Could not restore fee', message: e instanceof Error ? e.message : 'Unknown error' }),
  });

  // Array.isArray: a not-yet-restarted backend returns SPA HTML for a new route.
  const frozen = (Array.isArray(runsQ.data) ? runsQ.data : []).filter((r) => r.status === 'frozen');
  const waived: Exclusion[] = Array.isArray(exclQ.data) ? exclQ.data : [];
  const notDue: NotDue[] = Array.isArray(notDueQ.data) ? notDueQ.data : [];
  if (!frozen.length && !waived.length && !notDue.length) return null;   // nothing to bill / waive / defer

  // ONE unified, sorted list: each account-month is either 'to bill' (a frozen run) or
  // 'waived' (an exclusion), shown IN PLACE so waivers are visible at a glance rather
  // than hidden in a separate section (Pedro 2026-07-11). A waived row has no stored
  // amount (Option B deletes the run — we don't keep fees we don't charge), so its money
  // columns read "—".
  const agrOf = (r: { agreement_code: string | null; agreement_mnemonic: string | null }) =>
    [r.agreement_code, r.agreement_mnemonic].filter(Boolean).join(' · ') || '—';
  const FREQ_LABEL: Record<string, string> = { monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semiannual' };
  type URow =
    | { kind: 'bill'; key: string; sort: string; agr: string; acct: string; r: FeeHistoryRow }
    | { kind: 'waived'; key: string; sort: string; agr: string; acct: string; reason: string | null; exclusionId: string }
    | { kind: 'notdue'; key: string; sort: string; agr: string; acct: string; freq: string; nextDue: string };
  const rows: URow[] = [
    ...frozen.map((r): URow => ({
      kind: 'bill', key: `b-${r.run_id}`, sort: `${agrOf(r)}|${acctLabel(r)}`,
      agr: agrOf(r), acct: acctLabel(r), r,
    })),
    ...waived.map((e): URow => ({
      kind: 'waived', key: `w-${e.id}`, sort: `${agrOf(e)}|${acctLabel(e)}`,
      agr: agrOf(e), acct: acctLabel(e), reason: e.reason, exclusionId: e.id,
    })),
    ...notDue.map((n): URow => ({
      kind: 'notdue', key: `n-${n.account_id}`, sort: `${agrOf(n)}|${acctLabel(n)}`,
      agr: agrOf(n), acct: acctLabel(n), freq: n.fee_frequency, nextDue: n.next_due_month,
    })),
  ].sort((a, b) => (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs"><IconBan size={18} /><Text fw={600}>Review &amp; waive — {fmtMonthShort(period)}</Text></Group>
          <Group gap="xs">
            <Badge variant="light" color="blue">{frozen.length} to bill</Badge>
            {waived.length > 0 && <Badge variant="light" color="gray">{waived.length} waived</Badge>}
            {notDue.length > 0 && <Badge variant="outline" color="gray">{notDue.length} not due</Badge>}
          </Group>
        </Group>
        <Text size="xs" c="dimmed">
          Fees marked <b>To bill</b> will be billed and sent to the accountants. Waive any you will NOT bill
          (e.g. a first stub month) — a waived fee drops from every total and the export, shows a grey pill
          here, and can be restored. Accounts on a quarterly/semiannual cadence that aren't due this month
          show as <b>Not due</b> with the next billing month — they bill automatically then.
        </Text>

        <Table.ScrollContainer minWidth={720}>
          <Table withTableBorder striped verticalSpacing="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>Agreement</Table.Th>
              <Table.Th>Account</Table.Th>
              <Table.Th ta="right">Net</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th ta="right">Action</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.key}>
                  <Table.Td><Text size="sm">{row.agr}</Text></Table.Td>
                  <Table.Td><Text size="sm" c={row.kind === 'waived' ? 'dimmed' : undefined}>{row.acct}</Text></Table.Td>
                  {row.kind === 'bill' ? (
                    <>
                      <Table.Td ta="right"><Text size="sm">€{fmtEurFromCents(row.r.net_fee_cents)}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" fw={600}>€{fmtEurFromCents(row.r.total_fee_cents)}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">To bill</Text></Table.Td>
                      <Table.Td ta="right">
                        <Tooltip label="Waive — not billed" withArrow>
                          <ActionIcon variant="subtle" color="bordeaux" onClick={() => setWaiveRow(row.r)} aria-label="Waive fee">
                            <IconBan size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </>
                  ) : row.kind === 'waived' ? (
                    <>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={row.reason ?? ''} withArrow disabled={!row.reason}>
                          <Badge color="gray" variant="light" style={row.reason ? { cursor: 'help' } : undefined}>
                            Waived — not billed
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Tooltip label="Restore — make billable" withArrow>
                          <ActionIcon variant="subtle" color="verde" loading={restoreMut.isPending}
                            onClick={() => restoreMut.mutate(row.exclusionId)} aria-label="Restore fee">
                            <IconArrowBackUp size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </>
                  ) : (
                    <>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">—</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={`${FREQ_LABEL[row.freq] ?? row.freq} — off-cycle this month`} withArrow>
                          <Badge color="gray" variant="outline" style={{ cursor: 'help' }}>
                            Not due · next {fmtMonthShort(row.nextDue)}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td />
                    </>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>

      {waiveRow && (
        <WaiveModal
          row={waiveRow}
          submitting={waiveMut.isPending}
          onClose={() => setWaiveRow(null)}
          onSubmit={(reason) => waiveMut.mutate({ runId: waiveRow.run_id, reason })}
        />
      )}
    </Paper>
  );
}

function WaiveModal({ row, submitting, onClose, onSubmit }: {
  row: FeeHistoryRow; submitting: boolean; onClose: () => void; onSubmit: (reason: string | null) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal opened onClose={onClose} title="Waive fee — not billed" centered>
      <Stack gap="sm">
        <Text size="sm">
          Waive the fee for <b>{acctLabel(row)}</b> ({row.agreement_code || row.agreement_mnemonic || '—'}) for{' '}
          <b>{fmtMonthShort(row.period_month)}</b>. It will not be billed, drops out of every total and the
          accountant export, and can be restored later.
        </Text>
        <Textarea label="Reason (optional)" placeholder="e.g. started advising mid-month; first month waived"
          autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button color="bordeaux" loading={submitting} onClick={() => onSubmit(reason.trim() || null)}>
            Waive — not billed
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
